import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { navigationApi } from '../services/api';
import { activeDbSlug, isSlugFreePage } from '../services/activeDatabase';
import { isEntraAuth } from '../auth/authConfig';

/**
 * The navigation tree (AppCfg_Navigation) as the server filtered it for the person using this tab,
 * for the database the tab is on. The sidebar draws it, route guards ask it whether a page is
 * open, dashboards show the tiles it holds. The browser never decides access from permission keys:
 * a row the server marked open is a page the person may open, and anything else is not. (A row can
 * be in the tree without being open, when it shows only because something under it does.)
 *
 * Impersonation previews someone else, so the tree is fetched for them (?asUserId=). With auth off
 * the server knows no caller, so the tree is fetched for the identity picked on this machine.
 *
 * The last good tree per person and database is kept in localStorage: the sidebar paints from it at
 * once and a failed fetch keeps it. With no cached tree a failed fetch leaves an empty one (no page
 * open) and a retry.
 */
const NavigationContext = createContext(null);

const CACHE_PREFIX = 'dawn.navTree.';
const readCache = (key) => {
  try {
    const v = JSON.parse(window.localStorage.getItem(CACHE_PREFIX + key) || 'null');
    return Array.isArray(v) ? v : null;
  } catch { return null; }
};
const writeCache = (key, tree) => {
  try { window.localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(tree)); } catch { /* storage blocked or full */ }
};

/** The page part of a pathname: the whole path on a slug-free page, else the path after the database slug. */
export const pagePathOf = (pathname) => {
  const path = (pathname || '').replace(/^\/+/, '').replace(/\/+$/, '');
  if (isSlugFreePage(path)) return path;
  return path.split('/').slice(1).join('/');
};

const index = (tree) => {
  const byKey = new Map();
  const byDestination = new Map();
  const parents = new Map();
  const walk = (items, ancestors) => items.forEach(item => {
    byKey.set(item.id, item);
    if (item.destination && !byDestination.has(item.destination)) byDestination.set(item.destination, item);
    parents.set(item.id, ancestors);
    if (item.children?.length) walk(item.children, [...ancestors, item.id]);
  });
  walk(tree || [], []);
  return { byKey, byDestination, parents };
};

export const NavigationProvider = ({ children }) => {
  const { currentUser, isImpersonating, loading } = useAuth();
  const location = useLocation();

  const asUserId = currentUser?.id && (isImpersonating || !isEntraAuth()) ? currentUser.id : undefined;
  const who = currentUser?.id ? `${asUserId ? 'as' : 'me'}${currentUser.id}` : '';

  // The database follows the URL on a data page and the tab's own database on a slug-free page.
  const [slug, setSlug] = useState(() => activeDbSlug());
  useEffect(() => {
    const sync = () => setSlug(activeDbSlug());
    sync();
    window.addEventListener('pmd-database-changed', sync);
    return () => window.removeEventListener('pmd-database-changed', sync);
  }, [location.pathname]);

  const key = who ? `${who}.${slug || 'none'}` : '';
  const [state, setState] = useState({ key: '', tree: null, status: 'idle' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!key || loading) return undefined;
    let cancelled = false;
    const cached = readCache(key);
    setState(prev => (prev.key === key && prev.tree ? { ...prev, status: 'loading' } : { key, tree: cached, status: 'loading' }));
    navigationApi.tree(asUserId)
      .then(res => {
        if (cancelled) return;
        const tree = Array.isArray(res?.data) ? res.data : [];
        writeCache(key, tree);
        setState({ key, tree, status: 'ready' });
      })
      .catch(() => {
        if (cancelled) return;
        setState(prev => ({ key, tree: prev.key === key && prev.tree ? prev.tree : cached || [], status: 'failed' }));
      });
    return () => { cancelled = true; };
  }, [key, asUserId, loading, attempt]);

  const retry = useCallback(() => setAttempt(a => a + 1), []);

  const value = useMemo(() => {
    const current = state.key === key ? state : { tree: null, status: 'loading' };
    const tree = current.tree;
    const idx = index(tree);
    return {
      tree: tree || [],
      ready: tree !== null,
      failed: current.status === 'failed',
      retry,
      asUserId,
      scope: key,
      byKey: (id) => idx.byKey.get(id) || null,
      byDestination: (destination) => idx.byDestination.get(destination) || null,
      /** The person may open the page at this destination: its row is in the tree and open. */
      canOpen: (destination) => !!idx.byDestination.get(destination)?.open,
      /** Ids of the rows above a row, top first. */
      ancestorsOf: (id) => idx.parents.get(id) || [],
    };
  }, [state, key, retry, asUserId]);

  return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>;
};

export const useNavigation = () => {
  const ctx = useContext(NavigationContext);
  if (!ctx) throw new Error('useNavigation must be used inside NavigationProvider');
  return ctx;
};

/**
 * What the person may do on the tables a page edits, from GET /api/navigation/{navKey}/capabilities:
 * { navKey, download, tables: { [table]: { write, delete, bulkDelete, datasheet } } }, each verb
 * { allowed, reason }. The server answers with the same decision a save gets. Null while loading;
 * a failed fetch answers no to everything.
 */
export const useCapabilities = (navKey) => {
  const { asUserId, scope } = useNavigation();
  const [caps, setCaps] = useState(null);
  useEffect(() => {
    if (!navKey || !scope) return undefined;
    let cancelled = false;
    setCaps(null);
    navigationApi.capabilities(navKey, asUserId)
      .then(res => { if (!cancelled) setCaps(res.data); })
      .catch(() => { if (!cancelled) setCaps({ navKey, download: false, tables: {} }); });
    return () => { cancelled = true; };
  }, [navKey, asUserId, scope]);
  return caps;
};

/** One table's verbs out of a capabilities answer; every verb no when the table is not in it. */
export const tableCapabilities = (caps, tableName) => {
  const tables = caps?.tables || {};
  const hit = tables[tableName]
    || Object.entries(tables).find(([t]) => t.toLowerCase() === (tableName || '').toLowerCase())?.[1];
  const yes = (v) => !!hit?.[v]?.allowed;
  return { write: yes('write'), delete: yes('delete'), bulkDelete: yes('bulkDelete'), datasheet: yes('datasheet') };
};
